import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { BellRing } from "lucide-react";
import CreditTabs from "./CreditTabs";

const PURPLE = "#5B2CA0";
const DEEP = "#2B1E4A";

const SEV_COLOR = { critical: "#B4564F", high: "#C2560A", warning: "#B7891B", info: "#5B2CA0" };
const TYPE_LABEL = {
  SCORE_DROP: "Deterioração de score", NEW_OVERDUE: "Entrada em atraso", SEVERE_DELAY: "Atraso grave",
  ENTERED_HIGH_RISK: "Risco elevado", CLASS_CHANGE: "Mudança de classificação",
  LEFT_APPROVE: "Saiu de aprovação", ENTERED_BLOCKED: "Entrou em bloqueio",
  ENTERED_APPROVE: "Entrou em aprovação",
};

const EMPTY = { client_id: "", reseller_id: "", business_unit: "", type: "", severity: "", date_from: "", date_to: "" };

export default function CreditAlerts() {
  const [data, setData] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
    params.append("limit", "300");
    const { data } = await api.get(`/credit/alerts?${params.toString()}`);
    setData(data);
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const facets = data?.facets || {};
  return (
    <div className="p-6" data-testid="credit-alerts-page">
      <CreditTabs />
      <div className="mb-4 flex items-center gap-2">
        <BellRing size={22} color={PURPLE} />
        <div>
          <h1 className="font-display text-2xl font-bold" style={{ color: DEEP }}>Alertas de Crédito</h1>
          <p className="text-[12px] text-[#8A7788]">Sinais informativos do motor. Nenhum limite é reduzido automaticamente.</p>
        </div>
      </div>

      <section className="mb-4 rounded-2xl border border-[#EBE5DF] bg-white p-4" data-testid="alerts-filters">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Field label="Cliente (ID)"><input data-testid="filter-client" className="inp" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} /></Field>
          <Field label="Revendedora (ID)"><input data-testid="filter-reseller" className="inp" value={f.reseller_id} onChange={(e) => setF({ ...f, reseller_id: e.target.value })} /></Field>
          <Field label="Unidade">
            <select data-testid="filter-bu" className="inp" value={f.business_unit} onChange={(e) => setF({ ...f, business_unit: e.target.value })}>
              <option value="">Todas</option><option value="CRIS_LINGERIE">Cris Lingerie</option><option value="ELETROS">Eletros</option>
            </select>
          </Field>
          <Field label="Tipo">
            <select data-testid="filter-type" className="inp" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              <option value="">Todos</option>
              {(facets.type || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
            </select>
          </Field>
          <Field label="Severidade">
            <select data-testid="filter-severity" className="inp" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}>
              <option value="">Todas</option>
              {(facets.severity && facets.severity.length ? facets.severity : ["critical", "high", "warning", "info"]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="De"><input type="date" data-testid="filter-from" className="inp" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
          <Field label="Até"><input type="date" data-testid="filter-to" className="inp" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
          <div className="flex items-end"><button onClick={() => setF({ ...EMPTY })} className="rounded-lg border border-[#D6CFC7] px-3 py-2 text-[12px] font-bold text-[#5C5A57]" data-testid="filter-clear">Limpar</button></div>
        </div>
      </section>

      {!data ? <div className="text-sm text-[#8A7788]">Carregando...</div> : data.items.length === 0 ? (
        <div className="rounded-2xl border border-[#EBE5DF] bg-white p-6 text-center text-[13px] text-[#8A7788]" data-testid="alerts-empty">Nenhum alerta para os filtros selecionados.</div>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          <div className="text-[12px] text-[#8A7788]">{data.count} alerta(s)</div>
          {data.items.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#EBE5DF] bg-white px-4 py-3" data-testid={`alert-row-${a.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: SEV_COLOR[a.severity] || "#999" }} />
                <div>
                  <div className="text-[13px] font-bold" style={{ color: DEEP }}>{a.client_name} · <span style={{ color: SEV_COLOR[a.severity] }}>{TYPE_LABEL[a.type] || a.type}</span></div>
                  <div className="text-[11px] text-[#8A7788]">{a.message} · {a.business_unit}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#8A7788]">{new Date(a.created_at).toLocaleString("pt-BR")}</div>
            </div>
          ))}
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E0D6F0;border-radius:8px;padding:8px;font-size:13px}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="block text-[10px] uppercase tracking-widest text-[#8A7788] mb-1">{label}</label>{children}</div>
);
